import type { Color } from "@salestrak/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ErrorLike } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { type BoundsInput, MapClustersDocument, type MapClusterRow } from "#app/lib/data";

import type { MapPin, MapClusterBubble } from "#app/components/map/map-types";
import { getEntityGlyph } from "#app/lib/entity-visuals";

type GqlMapCluster = MapClusterRow;

// Map markers are Google-Maps SVG overlays that can't host an EntityTag, so
// these resolve glyph data through entity-visuals instead of reading entity
// icon fields inline.
export function getStageColor(items: GqlMapCluster["items"]): Color | undefined {
  for (const item of items) {
    const stage = item.entity?.stage;
    if (!stage) continue;
    const color = getEntityGlyph({ kind: "stage", id: stage.stageId, stage }).color;
    if (color) return color;
  }
  return undefined;
}

export function getWorkflowIcon(items: GqlMapCluster["items"]): string | undefined {
  const workflow = items[0]?.entity?.workflow;
  // Icon-less workflows keep plain dot pins — skip the kind-fallback icon.
  if (!workflow?.icon?.icon) return undefined;
  return (
    getEntityGlyph({ kind: "workflow", id: workflow.workflowId, workflow }).icon ??
    undefined
  );
}

export function clusterToPin(m: GqlMapCluster): MapPin {
  return {
    id: m.addressId ?? `pin-${m.latLng.lat},${m.latLng.lng}`,
    latitude: m.latLng.lat,
    longitude: m.latLng.lng,
    color: getStageColor(m.items) ?? "grey",
    count: m.count,
    icon: m.count === 1 ? getWorkflowIcon(m.items) : undefined,
    addressId: m.addressId ?? undefined,
    addressLine1: m.addressLine1 ?? undefined,
    wholeAddress: m.wholeAddress ?? undefined,
    issueIds: m.items
      .filter((i) => i.entity?.issueId)
      .map((i) => i.entity!.issueId!),
    propertyDetails: m.propertyDetails ?? undefined,
  };
}

export function useMapClusters(
  bounds: BoundsInput | null,
  zoom: number,
  where?: Record<string, unknown>,
  preserveIds?: string[],
) {
  const client = useApolloClient();
  const requestIdRef = useRef(0);
  const [rawData, setRawData] = useState<GqlMapCluster[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorLike | undefined>();

  // Round zoom so fractional changes that don't cross an integer boundary
  // don't trigger a redundant query
  const roundedZoom = Math.round(zoom);

  useEffect(() => {
    if (!bounds) return;

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);

    client
      .query({
        query: MapClustersDocument,
        variables: {
          bounds: {
            north: bounds.north,
            south: bounds.south,
            east: bounds.east,
            west: bounds.west,
          },
          where: where ?? undefined,
          options: {
            zoom: roundedZoom + 1,
            clusterThreshold: 15,
            ...(preserveIds?.length ? { preserveIds } : {}),
          },
        },
        fetchPolicy: "no-cache",
      })
      .then(({ data, error }) => {
        if (requestIdRef.current !== requestId) return;
        // errorPolicy "all" resolves with `error` set instead of rejecting.
        if (!data) {
          setError(error);
          setLoading(false);
          return;
        }
        setRawData(data.mapClusters);
        setLoading(false);
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        setError(err);
        setLoading(false);
      });
  }, [client, bounds, where, roundedZoom, preserveIds]);

  const pins: MapPin[] = useMemo(() => {
    if (!rawData.length) return [];
    return rawData
      .filter((m: GqlMapCluster) => !m.isCluster)
      .map(clusterToPin);
  }, [rawData]);

  const clusters: MapClusterBubble[] = useMemo(() => {
    if (!rawData.length) return [];
    return rawData
      .filter((m: GqlMapCluster) => m.isCluster)
      .map((m: GqlMapCluster) => ({
        id: `cluster-${m.latLng.lat},${m.latLng.lng}`,
        latitude: m.latLng.lat,
        longitude: m.latLng.lng,
        count: m.count,
        bounds: m.bounds ?? { north: 0, south: 0, east: 0, west: 0 },
      }));
  }, [rawData]);

  return { pins, clusters, loading, error };
}
